import React from 'react';

import { Link } from '@ekhodealer/ekho-common/rev';

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
const PLAIN_URL_REGEX = /(https?:\/\/[^\s<]*[^\s<.,;:!?)\]'"])/g;

/** Linkify plain URLs in a text segment that contains no markdown links. */
const linkifyPlainUrls = (text: string, keyPrefix: string): React.ReactNode[] => {
	const parts: React.ReactNode[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	PLAIN_URL_REGEX.lastIndex = 0;
	while ((match = PLAIN_URL_REGEX.exec(text)) !== null) {
		if (match.index > lastIndex) {
			parts.push(text.slice(lastIndex, match.index));
		}
		parts.push(
			<Link
				key={`${keyPrefix}-${match.index}`}
				href={match[0]}
				target='_blank'
				rel='noopener noreferrer'
			>
				{match[0]}
			</Link>,
		);
		lastIndex = match.index + match[0].length;
	}

	if (lastIndex < text.length) {
		parts.push(text.slice(lastIndex));
	}

	return parts;
};

/**
 * Parses markdown links `[text](url)` and plain URLs in a message body,
 * returning React nodes with clickable Rev Link elements.
 */
export const formatMessageBody = (body: string): React.ReactNode => {
	const parts: React.ReactNode[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	MARKDOWN_LINK_REGEX.lastIndex = 0;
	while ((match = MARKDOWN_LINK_REGEX.exec(body)) !== null) {
		if (match.index > lastIndex) {
			parts.push(...linkifyPlainUrls(body.slice(lastIndex, match.index), `pre-${match.index}`));
		}

		const [, linkText, url] = match;
		parts.push(
			<Link
				key={`md-${match.index}`}
				href={url}
				target='_blank'
				rel='noopener noreferrer'
			>
				{linkText}
			</Link>,
		);
		lastIndex = match.index + match[0].length;
	}

	if (lastIndex < body.length) {
		parts.push(...linkifyPlainUrls(body.slice(lastIndex), `post-${lastIndex}`));
	}

	if (parts.length === 0) {
		return body;
	}

	return <>{parts}</>;
};
